import CryptoJS from 'crypto-js';


const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY 
                    || process.env.ENCRYPTION_KEY 
                    || "dc58ba0d8a17a72360f978a51ccd8a710dd631875bef96468f572db0dd33439b"
            

/**
 * Encrypts data using AES encryption
 * @param {any} data - Data to encrypt (will be JSON stringified)
 * @returns {string} - Encrypted string in base64
 */
export function encryptData(data) {
  try {
    const jsonString = typeof data === 'string' ? data : JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY);
    return encrypted.toString();
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Decrypts AES encrypted data
 * @param {string} encryptedData - Encrypted string
 * @returns {any} - Decrypted and parsed data
 */
export function decryptData(encryptedData) {
  try {
    if (!encryptedData) {
      throw new Error('No data to decrypt');
    }
    
    const decrypted = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const jsonString = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!jsonString) {
      throw new Error('Decryption resulted in empty string');
    }
    
    // Try to parse as JSON, if it fails return the string
    try {
      return JSON.parse(jsonString);
    } catch {
      return jsonString;
    }
  } catch (error) {
    console.error('Decryption failed:', error);
    throw new Error('Failed to decrypt data');
  }
}

/**
 * Encrypts endpoint URL
 * @param {string} endpoint - The endpoint URL to encrypt
 * @returns {string} - Base64 URL-safe encrypted endpoint
 */
export function encryptEndpoint(endpoint) {
  try {
    const encrypted = CryptoJS.AES.encrypt(endpoint, ENCRYPTION_KEY);
    // Make it URL-safe by encoding
    return encodeURIComponent(encrypted.toString());
  } catch (error) {
    console.error('Endpoint encryption failed:', error);
    throw new Error('Failed to encrypt endpoint');
  }
}

/**
 * Decrypts endpoint URL
 * @param {string} encryptedEndpoint - Encrypted endpoint string
 * @returns {string} - Decrypted endpoint URL
 */
export function decryptEndpoint(encryptedEndpoint) {
  try {
    // Decode URL-safe string first
    const decoded = decodeURIComponent(encryptedEndpoint);
    const decrypted = CryptoJS.AES.decrypt(decoded, ENCRYPTION_KEY);
    const endpoint = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!endpoint) {
      throw new Error('Decryption resulted in empty endpoint');
    }
    
    return endpoint;
  } catch (error) {
    console.error('Endpoint decryption failed:', error);
    throw new Error('Failed to decrypt endpoint');
  }
}

/**
 * Creates an encrypted request payload
 * @param {string} endpoint - Original endpoint
 * @param {any} body - Request body
 * @param {string} method - HTTP method
 * @returns {object} - Encrypted payload object
 */
export function createEncryptedPayload(endpoint, body = null, method = 'GET') {
  return {
    ep: encryptEndpoint(endpoint), // encrypted endpoint
    data: body ? encryptData(body) : null, // encrypted data
    method: method
  };
}

/**
 * Decrypts an encrypted response
 * @param {object} response - Response object with encrypted data
 * @returns {any} - Decrypted response data
 */
export function decryptResponse(response) {
  if (!response || !response.data) {
    return response;
  }
  
  // If data is encrypted (check for 'encrypted' flag or structure)
  if (response.encrypted || (typeof response.data === 'string' && response.data.startsWith('U2'))) {
    try {
      return decryptData(response.data);
    } catch (error) {
      console.error('Response decryption failed:', error);
      // Return original response if decryption fails
      return response;
    }
  }
  
  return response.data;
}